import {Async, Empty} from 'Core/models/Async';
import {EmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ValidPassword} from 'Core/models/Password';
import {Lens} from 'monocle-ts';
import {compose} from 'redux';
import {createAction} from 'Shared/utilities/actionCreator';
import {IndependentAgentProfile} from 'Users/models/IndependentAgentProfile';
import {
  EmptyPasswordResetForm,
  PasswordResetForm,
  PasswordUpdate,
  validatePasswordResetForm,
  ValidPasswordResetForm,
} from 'Users/models/PasswordResetForm';

export enum PasswordRecoveryActionType {
  REQUEST_PASSWORD_RESET = 'passwordRecovery/REQUEST_PASSWORD_RESET',
  CHANGE_USERNAME = 'passwordRecovery/CHANGE_USERNAME',
  CHANGE_PASSWORD_RESET_FORM = 'passwordRecovery/CHANGE_PASSWORD_RESET_FORM',
  GET_PASSWORD_RESET_REQUEST_QUESTIONS = 'passwordRecovery/GET_PASSWORD_RESET_REQUEST_QUESTIONS',
  CHANGE_PASSWORD_RESET_REQUEST_QUESTIONS = 'passwordRecovery/CHANGE_PASSWORD_RESET_REQUEST_QUESTIONS',
  UPDATE_PASSWORD = 'passwordRecovery/UPDATE_PASSWORD',
}

const getNewUpdateFromEntry = compose(
  form => PasswordUpdate.Entry({form}),
  validatePasswordResetForm
);
const passwordResetFormLens = Lens.fromProp<PasswordResetForm>();
export const getPasswordResetRequestQuestions = (requestKey: string) =>
  createAction(PasswordRecoveryActionType.GET_PASSWORD_RESET_REQUEST_QUESTIONS, {requestKey});
export const changePasswordResetRequestQuestions = (questions: Async<IndependentAgentProfile>) =>
  createAction(PasswordRecoveryActionType.CHANGE_PASSWORD_RESET_REQUEST_QUESTIONS, questions);
export const changePasswordUpdate = (update: PasswordUpdate) =>
  createAction(PasswordRecoveryActionType.CHANGE_PASSWORD_RESET_FORM, {update});
export const changeAnswerOne = (answerOne: string, existingForm: PasswordResetForm) =>
  createAction(PasswordRecoveryActionType.CHANGE_PASSWORD_RESET_FORM, {
    update: getNewUpdateFromEntry(
      passwordResetFormLens('answerOne').set(NonEmptyString.create(answerOne))(existingForm)
    ),
  });
export const changeAnswerTwo = (answerTwo: string, existingForm: PasswordResetForm) =>
  createAction(PasswordRecoveryActionType.CHANGE_PASSWORD_RESET_FORM, {
    update: getNewUpdateFromEntry(
      passwordResetFormLens('answerTwo').set(NonEmptyString.create(answerTwo))(existingForm)
    ),
  });
export const changePassword = (passwordLine1: string, passwordLine2: string, existingForm: PasswordResetForm) =>
  createAction(PasswordRecoveryActionType.CHANGE_PASSWORD_RESET_FORM, {
    update: getNewUpdateFromEntry(
      passwordResetFormLens('password').set(ValidPassword.create(passwordLine1, passwordLine2))(existingForm)
    ),
  });
export const requestPasswordReset = (username: NonEmptyString) =>
  createAction(PasswordRecoveryActionType.REQUEST_PASSWORD_RESET, {username});
export const changeUsername = (username: StringUnion) =>
  createAction(PasswordRecoveryActionType.CHANGE_USERNAME, {username});
export const updatePassword = (
  id: string,
  requestKey: string,
  independentAgentProfile: IndependentAgentProfile,
  passwordResetForm: ValidPasswordResetForm
) =>
  createAction(PasswordRecoveryActionType.UPDATE_PASSWORD, {
    id,
    requestKey,
    independentAgentProfile,
    passwordResetForm,
  });

export type PasswordRecoveryState = {
  username: StringUnion;
  passwordResetRequest: Async<IndependentAgentProfile>;
  passwordUpdate: PasswordUpdate;
};

type PasswordRecoveryAction =
  | ReturnType<typeof changeUsername>
  | ReturnType<typeof changePasswordResetRequestQuestions>
  | ReturnType<typeof changeAnswerOne>
  | ReturnType<typeof changeAnswerTwo>
  | ReturnType<typeof changePassword>;

export const DEFAULT_STATE: PasswordRecoveryState = {
  username: new EmptyString(),
  passwordUpdate: PasswordUpdate.Entry({form: EmptyPasswordResetForm}),
  passwordResetRequest: new Empty(),
};

const PasswordRecoveryLens = Lens.fromProp<PasswordRecoveryState>();
export default (
  state: PasswordRecoveryState = DEFAULT_STATE,
  action: PasswordRecoveryAction
): PasswordRecoveryState => {
  switch (action.type) {
    case PasswordRecoveryActionType.CHANGE_PASSWORD_RESET_REQUEST_QUESTIONS:
      return PasswordRecoveryLens('passwordResetRequest').set(action.payload)(state);
    case PasswordRecoveryActionType.CHANGE_PASSWORD_RESET_FORM:
      return PasswordRecoveryLens('passwordUpdate').set(action.payload.update)(state);
    case PasswordRecoveryActionType.CHANGE_USERNAME:
      return PasswordRecoveryLens('username').set(action.payload.username)(state);
    default:
      return state;
  }
};
